import styled, { css, keyframes } from 'styled-components'
import { Modal } from 'antd'

const highlightAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 4px rgba(44, 101, 172, 0.4);
  }
  100% {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`

// Theme color constants
export const colors = {
  primary: '#2C65AC',
  primaryHover: '#1E4E85',
  primaryLight: '#E8F1FC',
  bg: '#F8FAFC',
  cardBg: '#FFFFFF',
  textPrimary: '#1E293B',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  border: '#E2E8F0',
  hoverBg: '#F1F5F9',
  success: '#10B981',
  danger: '#EF4444',
  warning: '#F59E0B',
  shadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  shadowHover: '0 12px 20px rgba(0, 0, 0, 0.08)',
  radius: '12px',
  radiusLg: '16px',
}

export const PageWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 130px);
  background: #ffffff;
  border-radius: 16px;
  padding: 16px 12px 24px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;

  @media (max-width: 900px) {
    padding: 12px 8px;
    border-radius: 12px;
  }
`

/** 3 cột sát 2 mép, giảm khoảng trắng giữa */
export const PageContentInner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  background: transparent;

  @media (max-width: 1200px) {
    gap: 10px;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 16px;
  }
`

export const RecentPhotosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  border-radius: 10px;
  overflow: hidden;

  .nf-recent-photo {
    position: relative;
    aspect-ratio: 1;
    background: #e4e6eb;
    cursor: pointer;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      transition: transform 0.25s ease;
    }

    &:hover img {
      transform: scale(1.06);
    }
  }

  .nf-recent-photo-more {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

export const FeedPulseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .nf-pulse-item {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    cursor: pointer;
    padding: 8px;
    margin: -8px;
    border-radius: 10px;
    transition: background 0.15s ease;

    &:hover {
      background: ${colors.hoverBg};
    }
  }

  .nf-pulse-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    background: linear-gradient(135deg, ${colors.primary} 0%, #4facfe 100%);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .nf-pulse-body {
    min-width: 0;
    flex: 1;
  }

  .nf-pulse-name {
    font-size: 13px;
    font-weight: 600;
    color: ${colors.textPrimary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .nf-pulse-text {
    font-size: 12px;
    color: ${colors.textSecondary};
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 2px;
  }

  .nf-pulse-time {
    font-size: 11px;
    color: ${colors.textMuted};
    margin-top: 3px;
  }
`

// Side columns (trái / phải) — sticky kiểu Facebook
export const SidebarLeft = styled.div`
  width: 280px;
  flex: 0 0 280px;
  position: sticky;
  top: 50px;
  height: fit-content;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;

  @media (max-width: 1200px) {
    width: 240px;
    flex-basis: 240px;
  }

  @media (max-width: 1100px) {
    display: none; /* màn hẹp: ẩn cột trái, giữ feed + menu phải */
  }

  @media (max-width: 900px) {
    display: flex;
    width: 100%;
    flex-basis: auto;
    position: static;
    max-height: none;
    order: -1;
  }
`

export const SidebarRight = styled.div`
  width: 280px;
  flex: 0 0 280px;
  position: sticky;
  top: 50px;
  height: fit-content;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;

  @media (max-width: 1200px) {
    width: 240px;
    flex-basis: 240px;
  }

  @media (max-width: 900px) {
    width: 100%;
    flex-basis: auto;
    position: static;
    max-height: none;
  }
`

export const SidebarCard = styled.div`
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg};
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 16px;
`

export const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: ${colors.cardBg};
  border-radius: ${colors.radius};
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: ${colors.shadowHover};
    border-color: ${colors.primary};
  }
`

export const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primary} 0%, #4facfe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(44, 101, 172, 0.2);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const UserInfo = styled.div`
  overflow: hidden;
`

export const UserName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const UserDept = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`

export const SidebarMenu = styled.div`
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg}; /* 16px border radius */
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const SidebarItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px; /* More rounded items */
  cursor: pointer;
  font-size: 14px;
  color: ${colors.textPrimary};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.hoverBg};
    transform: translateX(4px);
  }

  ${props =>
    props.active &&
    css`
      background: ${colors.primaryLight};
      color: ${colors.primary};
      font-weight: 600;

      &:hover {
        background: ${colors.primaryLight};
        color: ${colors.primary};
        transform: none;
      }
    `}
`

export const SidebarIcon = styled.span`
  font-size: 18px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

// Main Feed — cột giữa kiểu Facebook (~680px)
export const MainFeed = styled.div`
  flex: 0 1 680px;
  min-width: 0;
  max-width: 680px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    max-width: 100%;
  }
`

// Composer (Post Create)
export const ComposerContainer = styled.div`
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg};
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 20px;
  transition: all 0.2s ease;

  &:hover,
  &.focused {
    box-shadow: ${colors.shadowHover};
    border-color: rgba(44, 101, 172, 0.4);
  }
`

export const ComposerHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

export const ComposerInput = styled.textarea`
  flex: 1;
  border: none;
  resize: none;
  outline: none;
  font-size: 15px;
  color: ${colors.textPrimary};
  padding: 8px 0;
  min-height: 44px;
  line-height: 1.5;
  background: transparent;

  &::placeholder {
    color: ${colors.textSecondary};
  }

  &:disabled {
    cursor: not-allowed;
  }
`

export const ComposerActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${colors.border};

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`

export const ComposerActionButtons = styled.div`
  display: flex;
  gap: 8px;
`

export const ComposerActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid transparent;
  background: transparent;
  color: ${colors.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.hoverBg};
    color: ${colors.textPrimary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    font-size: 16px;
  }

  &.image {
    color: ${colors.success};
    &:hover {
      background: rgba(16, 185, 129, 0.1);
    }
  }

  &.video {
    color: ${colors.primary};
    &:hover {
      background: rgba(44, 101, 172, 0.1);
    }
  }
`

export const ComposerSubmitButton = styled.button`
  padding: 8px 24px;
  border-radius: 20px;
  border: none;
  background: ${colors.border};
  color: ${colors.textSecondary};
  font-weight: 600;
  font-size: 14px;
  cursor: not-allowed;
  transition: all 0.2s ease;

  &.active {
    background: ${colors.primary};
    color: #ffffff;
    cursor: pointer;
    box-shadow: 0 4px 10px rgba(44, 101, 172, 0.3);

    &:hover {
      background: ${colors.primaryHover};
      transform: translateY(-1px);
    }
  }

  &:disabled {
    opacity: 0.6;
  }
`

// Media preview in composer
export const ComposerMediaPreview = styled.div`
  margin-top: 14px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${colors.border};
`

export const ComposerPreviewGrid = styled.div`
  display: grid;
  gap: 4px;
  background: transparent;

  &.grid-1 {
    grid-template-columns: 1fr;
  }
  &.grid-2 {
    grid-template-columns: 1fr 1fr;
  }
  &.grid-3 {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: 1fr 1fr;
  }
  &.grid-4 {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: 1fr 1fr;
  }
`

export const ComposerPreviewItem = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f0f2f5;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

export const ComposerPreviewMain = styled.div`
  position: relative;
  width: 100%;
  min-height: 280px;
  max-height: 480px;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img,
  video {
    width: 100%;
    height: 100%;
    max-height: 480px;
    object-fit: contain;
    display: block;
  }
`

export const ComposerPreviewNavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;

  &.prev {
    left: 8px;
  }
  &.next {
    right: 8px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.75);
  }
`

export const ComposerPreviewThumbs = styled.div`
  display: flex;
  gap: 6px;
  padding: 8px;
  overflow-x: auto;
  background: #fff;
  border-top: 1px solid ${colors.border};
`

export const ComposerPreviewThumb = styled.div`
  position: relative;
  flex: 0 0 64px;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  background: #f0f2f5;

  &.active {
    border-color: ${colors.primary};
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

export const ComposerPreviewCaption = styled.div`
  padding: 12px 14px 10px;
  border-top: 1px solid ${colors.border};
  background: #fafbfc;

  .nf-caption-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  .nf-caption-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.textPrimary};
  }

  .nf-caption-label-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${colors.primary};
    flex-shrink: 0;
  }

  .nf-caption-meta {
    font-size: 12px;
    color: ${colors.textMuted};
    font-variant-numeric: tabular-nums;
  }

  .nf-caption-meta.warn {
    color: ${colors.warning};
  }

  .ant-input {
    border-radius: 10px !important;
    border: 1px solid ${colors.border} !important;
    background: #fff !important;
    box-shadow: none !important;
    font-size: 14px;
    line-height: 1.45;
    padding: 10px 12px !important;
    color: ${colors.textPrimary};
    resize: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
      color: ${colors.textMuted};
    }

    &:hover {
      border-color: #cbd5e1 !important;
    }

    &:focus {
      border-color: ${colors.primary} !important;
      box-shadow: 0 0 0 3px ${colors.primaryLight} !important;
    }
  }
`

export const ComposerPreviewRemove = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`

// Post Card
export const PostCardContainer = styled.div`
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg};
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: box-shadow 0.2s ease-in-out;

  &.nf-post-highlight {
    animation: ${highlightAnimation} 2s ease-out;
  }

  &:hover {
    box-shadow: ${colors.shadowHover};
  }
`

export const PostCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const PostAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

export const PostMeta = styled.div`
  display: flex;
  flex-direction: column;
`

export const PostAuthorName = styled.div`
  font-weight: 600;
  font-size: 15px;
  color: ${colors.textPrimary};

  &:hover {
    color: ${colors.primary};
    text-decoration: underline;
  }
`

export const PostTime = styled.div`
  font-size: 12px;
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
`

export const PostMenuBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.hoverBg};
    color: ${colors.textPrimary};
  }
`

export const PostContent = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: ${colors.textPrimary};
  word-break: break-word;

  p {
    margin: 0 0 6px 0;
    line-height: 1.6;
    word-break: break-word;

    &:last-child {
      margin-bottom: 0;
    }
  }

  blockquote {
    border-left: 3px solid ${colors.primary};
    background: #f0f7ff;
    padding: 6px 12px;
    margin: 6px 0;
    font-style: italic;
    border-radius: 0 6px 6px 0;
  }

  ul,
  ol {
    padding-left: 22px;
    margin: 4px 0 8px 0;
  }

  a {
    color: ${colors.primary};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  .mention {
    color: ${colors.primary};
    font-weight: 600;
    background: rgba(24, 119, 242, 0.08);
    border-radius: 4px;
    padding: 1px 4px;
    text-decoration: none;
  }
`

export const PostMedia = styled.div`
  border-radius: ${colors.radius};
  overflow: hidden;
  border: 1px solid ${colors.border};
`

// Media Grid layout
export const MediaGridContainer = styled.div`
  display: grid;
  gap: 2px;
  background: #000;
  width: 100%;

  &.grid-1 {
    grid-template-columns: 1fr;
  }
  &.grid-2 {
    grid-template-columns: 1fr 1fr;
  }
  &.grid-3 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 220px 220px;
  }
  &.grid-4 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 200px 200px;
  }
  /* 5+ ảnh: 2 trên / 3 dưới (kiểu Facebook) — dùng 6 cột để chia đều */
  &.grid-5 {
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: 280px 200px;
  }
`

export const MediaItem = styled.div`
  position: relative;
  cursor: pointer;
  overflow: hidden;
  width: 100%;
  background: #f0f2f5;

  &.grid-1 {
    height: auto;
    max-height: 520px;
    overflow: hidden;
  }

  .grid-2 & {
    height: 340px;
  }

  .grid-3 & {
    height: 220px;
  }

  .grid-3 &.grid-3-0 {
    grid-row: span 2;
    height: 442px;
  }

  .grid-4 & {
    height: 200px;
  }

  .grid-5 & {
    height: 200px;
    grid-column: span 2;
  }

  .grid-5 &.grid-5-top {
    height: 280px;
    grid-column: span 3;
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    background: #f0f2f5;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }
`

export const MediaOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 24px;
  font-weight: 700;
`

export const PostStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: ${colors.textSecondary};
  border-bottom: 1px solid ${colors.border};
`

export const StatLikes = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const StatLikeIcon = styled.span`
  background: #1877f2;
  color: #ffffff;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  line-height: 1;

  svg {
    transform: translateY(-0.5px);
  }
`

export const StatComments = styled.button`
  border: none;
  background: transparent;
  color: ${colors.textSecondary};
  cursor: pointer;
  font-size: 13px;

  &:hover {
    text-decoration: underline;
    color: ${colors.primary};
  }
`

export const PostActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
`

export const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${colors.textSecondary};
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.hoverBg};
    color: ${colors.textPrimary};
  }

  &.liked {
    color: #1877f2;
  }

  svg {
    font-size: 16px;
  }
`

// Comments Section
export const CommentSectionContainer = styled.div`
  padding-top: 16px;
  border-top: 1px solid ${colors.border};
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const CommentInputWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`

export const CommentInputBox = styled.div`
  flex: 1;
  display: flex;
  border: 1px solid ${colors.primaryHover};
  border-radius: 20px;
  padding: 6px 12px 6px 16px;
  align-items: center;
  transition: all 0.2s;

  &:focus,
  &:focus-within {
    background: #ffffff;
    outline: 1px solid ${colors.primary};
    box-shadow: 0 0 0 2px ${colors.primaryLight};
  }
`

export const CommentInputField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: ${colors.textPrimary};
  padding: 4px 0;
`

export const CommentSendButton = styled.button`
  border: none;
  background: transparent;
  color: ${colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: ${colors.border};
    color: ${colors.textPrimary};
  }

  &.active {
    color: ${colors.primary};
  }
`

// Comment Item style
export const CommentItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;

  &.nf-comment-highlight {
    animation: ${highlightAnimation} 2.5s ease-out;
    background-color: rgba(44, 101, 172, 0.05);
    padding: 6px;
  }
`

export const CommentBody = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`

export const CommentBubble = styled.div`
  background: ${colors.hoverBg};
  border-radius: 18px;
  padding: 10px 16px;
  max-width: 85%;
`

export const CommentAuthor = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: ${colors.textPrimary};
  margin-bottom: 2px;
  white-space: nowrap;
`

export const CommentText = styled.div`
  font-size: 14px;
  color: ${colors.textPrimary};
  line-height: 1.4;
`

export const CommentActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: ${colors.textSecondary};
  padding-left: 12px;
  margin-top: 2px;
`

export const CommentActionBtn = styled.button`
  border: none;
  background: transparent;
  color: ${colors.textSecondary};
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
    color: ${colors.textPrimary};
  }

  &.liked {
    color: ${colors.primary};
  }
`

export const CommentTime = styled.span`
  color: ${colors.textMuted};
`

export const CommentLikeBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  background: #ffffff;
  padding: 2px 6px;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-size: 11px;
  font-weight: 500;
`

export const CommentMoreBtn = styled.button`
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: 0 4px;
  display: flex;
  align-items: center;

  &:hover {
    color: ${colors.textPrimary};
  }
`

// Gallery Photos / Videos Grid
export const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg};
  padding: 20px;
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
`

export const GalleryItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${colors.radius};
  overflow: hidden;
  border: 1px solid ${colors.border};
  cursor: pointer;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.3s ease;
  }

  &:hover img,
  &:hover video {
    transform: scale(1.05);
  }

  .video-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 40px;
    color: rgba(255, 255, 255, 0.9);
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4));
  }
`

// Empty tab messages
export const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg};
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .icon {
    font-size: 48px;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    color: ${colors.textPrimary};
    margin-bottom: 8px;
    font-weight: 600;
  }

  p {
    font-size: 14px;
    color: ${colors.textSecondary};
    max-width: 400px;
    margin: 0;
  }
`

// Skeleton Loader styles
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`

export const ShimmerAnimation = css`
  background: #f6f7f8;
  background-image: linear-gradient(
    to right,
    #f6f7f8 0%,
    #edeef1 20%,
    #f6f7f8 40%,
    #f6f7f8 100%
  );
  background-repeat: no-repeat;
  background-size: 800px 104px;
  animation: ${shimmer} 1.2s linear infinite forwards;
`

export const SkeletonPostContainer = styled.div`
  background: ${colors.cardBg};
  border-radius: ${colors.radiusLg};
  box-shadow: ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const SkeletonHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`

export const SkeletonAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  ${ShimmerAnimation}
`

export const SkeletonMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`

export const SkeletonName = styled.div`
  height: 14px;
  width: 120px;
  border-radius: 4px;
  ${ShimmerAnimation}
`

export const SkeletonTime = styled.div`
  height: 10px;
  width: 80px;
  border-radius: 4px;
  ${ShimmerAnimation}
`

export const SkeletonContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const SkeletonLine = styled.div`
  height: 12px;
  border-radius: 4px;
  ${ShimmerAnimation}
`

export const SkeletonImage = styled.div`
  height: 240px;
  border-radius: ${colors.radius};
  ${ShimmerAnimation}
`

export const SkeletonActions = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.border};
  padding-top: 12px;
`

export const SkeletonAction = styled.div`
  height: 28px;
  width: 80px;
  border-radius: 4px;
  ${ShimmerAnimation}
`

// Lightbox Viewer
export const LightboxOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.92);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;

  &.with-sidebar {
    align-items: stretch;
    justify-content: stretch;
    background: #000;
  }
`

export const PhotoViewerLayout = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
`

export const PhotoViewerMediaPane = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

export const PhotoViewerSidebar = styled.div`
  width: 400px;
  max-width: 40vw;
  background: #18191a;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #3a3b3c;
  z-index: 10002;
  color: #e4e6eb;

  @media (max-width: 900px) {
    width: 320px;
    max-width: 45vw;
  }

  /* Dark theme overrides cho CommentSection / CommentItem bên trong */
  .nf-photo-comments-dark {
    color: #e4e6eb;

    textarea,
    input {
      color: #e4e6eb !important;
      background: transparent !important;
    }

    textarea::placeholder,
    input::placeholder {
      color: #b0b3b8 !important;
    }
  }

  ${CommentSectionContainer} {
    border-top-color: #3a3b3c;
    color: #e4e6eb;
  }

  ${CommentInputBox} {
    border-color: #3a3b3c;
    background: #242526;

    &:focus,
    &:focus-within {
      background: #242526;
      outline: 1px solid #2c65ac;
      box-shadow: none;
    }
  }

  ${CommentInputField} {
    color: #e4e6eb;
  }

  ${CommentBubble} {
    background: #3a3b3c;
    color: #e4e6eb;
  }

  ${CommentAuthor},
  ${CommentText} {
    color: #e4e6eb;
  }

  ${CommentActionsRow},
  ${CommentActionBtn},
  ${CommentTime} {
    color: #b0b3b8;
  }

  ${CommentActionBtn}:hover {
    color: #e4e6eb;
    background: #3a3b3c;
  }

  ${CommentItemContainer} {
    &.nf-comment-highlight {
      background-color: rgba(44, 101, 172, 0.25);
    }
  }

  /* Ant Design controls trong sidebar tối */
  .ant-input,
  .ant-input-affix-wrapper,
  textarea.ant-input {
    background: #242526 !important;
    border-color: #3a3b3c !important;
    color: #e4e6eb !important;
  }

  .ant-btn-default {
    background: #3a3b3c;
    border-color: #3a3b3c;
    color: #e4e6eb;
  }

  .ant-btn-default:hover {
    background: #4e4f50;
    border-color: #4e4f50;
    color: #fff;
  }
`

export const LightboxCloseBtn = styled.button`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10003;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`

export const LightboxCounter = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
  z-index: 10001;

  &.in-pane {
    top: 16px;
  }
`

export const LightboxNavBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  &.prev {
    left: 16px;
  }
  &.next {
    right: 16px;
  }

  &.in-pane.prev {
    left: 12px;
  }
  &.in-pane.next {
    right: 12px;
  }
`

export const LightboxContent = styled.div`
  max-width: 90vw;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 4px;
    user-select: none;
  }

  &.in-pane {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
    padding: 48px 64px 72px;

    img {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }
`

export const LightboxThumbs = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10001;

  &.in-pane {
    bottom: 12px;
  }
`

export const LightboxThumb = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  opacity: 0.6;
  transition: all 0.2s;

  &.active {
    border-color: #ffffff;
    opacity: 1;
  }

  &:hover {
    opacity: 0.9;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

// Loading more / End message
export const LoadingMore = styled.div`
  text-align: center;
  padding: 20px;
`

export const EndMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${colors.textSecondary};
  font-size: 14px;

  .icon {
    font-size: 32px;
    margin-bottom: 8px;
  }
`

// SeeMore button for post content
export const SeeMoreBtn = styled.button`
  border: none;
  background: transparent;
  color: ${colors.primary};
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  padding: 0;
  margin-top: 4px;

  &:hover {
    text-decoration: underline;
  }
`

/* Premium Rounded Reactions Modal */
export const ReactionsModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 16px !important;
    overflow: hidden !important;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.15) !important;
    border: none !important;
    background: #ffffff !important;
  }

  .ant-modal-close {
    top: 12px !important;
    right: 12px !important;
    color: #65676b !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    border-radius: 50% !important;
    background: #f0f2f5 !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    &:hover {
      background: #e4e6eb !important;
      color: #050505 !important;
      transform: scale(1.05);
    }

    .ant-modal-close-x {
      width: 32px !important;
      height: 32px !important;
      line-height: 32px !important;
      font-size: 14px !important;
    }
  }

  .ant-tabs-nav {
    margin: 0 !important;
  }

  .ant-tabs-tab {
    padding: 8px 12px !important;
    margin: 0 4px 0 0 !important;
    transition: all 0.2s ease !important;
    border-radius: 8px !important;

    &:hover {
      background: #f1f5f9 !important;
      color: ${colors.primary} !important;
    }
  }

  .ant-tabs-tab-active {
    background: ${colors.primaryLight} !important;
    color: ${colors.primary} !important;
  }

  .ant-tabs-ink-bar {
    background-color: ${colors.primary} !important;
    height: 3px !important;
    border-radius: 3px 3px 0 0 !important;
  }

  .hoverable-list-item {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    margin: 2px 0 !important;
    border-radius: 8px !important;

    &:hover {
      background: #f1f5f9 !important;
      transform: translateX(4px);
    }
  }

  .reactions-modal-list {
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #cbd5e1;
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: #94a3b8;
    }
  }
`
